// tslint:disable: no-output-on-prefix
import { Component, EventEmitter, Output, Input, OnChanges, SimpleChanges, HostAttributeToken, inject } from '@angular/core';
import { MultiMediaItem, ToasterService } from '@lc/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { DatePipe } from '@angular/common';
import { E2eIdDirective } from '@lc/ui';
import { BaseDialog } from '../../../../../ui/src/lib/dialogs/base/base.dialog';
import { MultiMediaItemFormComponent } from '../multi-media-item-form/multi-media-item-form.component';
import { CheckboxComponent } from '../../../../../ui/src/lib/inputs/checkbox/checkbox.component';
import { MultiMediaItemForm } from '../multi-media-item-form/multi-media-item.form';

class MultimediaViewModel {
  constructor(public item: MultiMediaItem, public isSelected: boolean = false) {
  }
}

@Component({
  selector: 'lc-multimedia-table',
  templateUrl: './multimedia-table.component.html',
  styleUrls: ['./multimedia-table.component.scss'],
  imports: [
    CheckboxComponent,
    FormsModule,
    MatButton,
    MatIcon,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIconButton,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    BaseDialog,
    MultiMediaItemFormComponent,
    ReactiveFormsModule,
    DatePipe,
    E2eIdDirective,
  ],
})
export class MultimediaTableComponent implements OnChanges {
  private matDialog = inject(MatDialog);
  private readonly toasterService = inject(ToasterService);

  private dialogRef: MatDialogRef<any> | null = null;
  readonly rows: string[];

  @Input()
    name = 'selectedMM';

  @Input()
    type = 'video';

  @Input()
    items: MultiMediaItem[] | null;

  @Input()
    selectedItems: MultiMediaItem[] = [];

  @Input()
    multiselect = true;

  @Input()
    maxSelections: number | null = null;

  @Input()
    hideDatetime = false;

  @Input()
    truncateUrl = false;

  @Output()
  readonly onUpdate = new EventEmitter<MultiMediaItem>();

  @Output()
  readonly onDelete = new EventEmitter<MultiMediaItem[]>();

  @Output()
  readonly onSelection = new EventEmitter<MultiMediaItem[]>();

  @Output()
  readonly onChange = new EventEmitter<MultiMediaItem[]>();

  formGroup = new MultiMediaItemForm();

  editItem: MultiMediaItem;

  deleteEnabled = true;
  selectEnabled = true;
  editEnabled = true;
  isProduct = false;

  viewModels: MultimediaViewModel[] = [];
  allSelected = false;
  noneSelected = true;

  isCheckboxDisabled(vm: MultimediaViewModel): boolean {
    if (!this.multiselect || !this.maxSelections) {
      return false;
    }
    const currentlySelected = this.viewModels.filter((v) => v.isSelected).length;
    return !vm.isSelected && currentlySelected >= this.maxSelections;
  }

  constructor() {
    const deleteEnabled = inject(new HostAttributeToken('deleteEnabled'), { optional: true });
    const selectEnabled = inject(new HostAttributeToken('selectEnabled'), { optional: true });
    const editEnabled = inject(new HostAttributeToken('editEnabled'), { optional: true });
    const isProduct = inject(new HostAttributeToken('isProduct'), { optional: true });

    this.deleteEnabled = (deleteEnabled === null) || (deleteEnabled === 'true');
    this.selectEnabled = (selectEnabled === null) || (selectEnabled === 'true');
    this.editEnabled = (editEnabled === null) || (editEnabled === 'true');
    this.isProduct = isProduct && (isProduct === 'true');
    this.rows = [
      ...(this.selectEnabled ? ['select'] : []),
      'name', 'link', 'date', 'agent',
      ...(this.editEnabled || this.deleteEnabled ? ['actions'] : []),
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.items && this.items != null) {
      // Query the items, sort by date descending and map into an appropriate view model
      this.viewModels = this.items
        .sort((a, b) => b.audit.updatedAt.getTime() - a.audit.updatedAt.getTime())
        .map((item) => new MultimediaViewModel(
          item,
          this.selectedItems.filter((mmedia) => mmedia.title === item.title && mmedia.uri === item.uri).length > 0,
        ));

      if (!this.isProduct && !changes.items.firstChange) {
        this.onSelectionChanged();
      }
    }
    if (changes.selectedItems) {
      // when selections change, fix the selections on the view models which is the wrapper on items
      changes.selectedItems.currentValue.forEach((item) => {
        const match = this.viewModels.find((vm) => vm.item._id === item._id);
        if (match) {
          match.isSelected = true;
        }
      });
    }
  }

  /**
   * Queries the selected ViewModels and sets the appropriate properties needed for the UI
   * to determine the status of the Check All checkbox and Delete Selected button
   * @param selectedIndex 0-n for the selection.
   */
  onSelectionChanged(selectedIndex?: number) {
    this.allSelected = this.viewModels.every((vm) => vm.isSelected);
    this.noneSelected = this.viewModels.every((vm) => !vm.isSelected);
    if (!this.multiselect && selectedIndex >= 0) {
      this.viewModels.forEach((vm, index) => {
        vm.isSelected = index === selectedIndex;
      });
    }
    this.selectedItems = this.viewModels.filter((vm) => vm.isSelected).map((vm) => vm.item);
    this.onSelection.emit(this.selectedItems);
  }

  /**
   * Queries the selected ViewModels and sets the appropriate properties needed for the UI
   * to determine the status of the Check All checkbox and Delete Selected button
   * @param selectedIndex 0-n for the selection.
   */
  onMouseClick(selectedIndex?: number) {
    this.allSelected = this.viewModels.every((vm) => vm.isSelected);
    this.noneSelected = this.viewModels.every((vm) => !vm.isSelected);
    if (!this.multiselect && selectedIndex >= 0) {
      this.viewModels.forEach((vm, index) => {
        if (vm.isSelected && index === selectedIndex) {
          vm.isSelected = false;
        } else {
          vm.isSelected = index === selectedIndex;
        }
      });
    } else if (this.multiselect && selectedIndex >= 0) {
      const vm = this.viewModels[selectedIndex];
      const currentlySelected = this.viewModels.filter((v) => v.isSelected).length;

      // If trying to select (not deselect) and already at max limit, prevent selection
      if (!vm.isSelected && this.maxSelections && currentlySelected >= this.maxSelections) {
        return; // Don't allow selecting more
      }

      // Toggle the selection
      vm.isSelected = !vm.isSelected;
    }
    this.selectedItems = this.viewModels.filter((vm) => vm.isSelected).map((vm) => vm.item);
    this.onChange.emit(this.selectedItems);
  }
  /**
   * Toggles the status of all of the multimedia links. If any of the links are selected, toggle
   * will deselect all links. If none are selected, it will select all the links.
   */
  toggleAll() {
    const checkAll = this.viewModels.every((vm) => vm.isSelected) || this.viewModels.every((vm) => !vm.isSelected);
    if (checkAll || this.allSelected) {
      this.viewModels.forEach((vm) => vm.isSelected = this.allSelected);
      this.onSelectionChanged();
    }
  }

  /**
   * Queries all selected ViewModels and will select the appropriate link. It will then emit the
   * onDelete event with the selected multimedia links
   */
  deleteSelected() {
    const selectedItems = this.viewModels.filter((vm) => vm.isSelected).map((vm) => vm.item);
    this.onDelete.emit(selectedItems);
  }

  open(modal, item) {
    this.editItem = item;
    this.formGroup.patchValue(item);
    this.dialogRef = this.matDialog.open(modal, { width: '500px', maxWidth: '90vw', maxHeight: '90vh' });
  }

  doUpdate() {
    const { uri } = this.formGroup.value;

    if (uri !== this.editItem.uri && this.items.some((item) => item.uri === uri)) {
      this.toasterService.showError('Cannot submit a duplicate link');
      return;
    }

    Object.assign(this.editItem, this.formGroup.value);
    this.onUpdate.emit(this.editItem);
    this.close();
  }

  onCancel() {
    this.close();
  }

  close() {
    this.dialogRef.close();
    this.editItem = null;
    this.formGroup.reset();
  }
}
